import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import type { PendingBrowserLaneSignIn } from '../services/composio/browserLaneRoster';
import styled, { keyframes } from 'styled-components';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { collection, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { ACCOUNT_FIRESTORE_PREFIX } from '../services/prest0nRuntime/constants';
import { getUserUiLanguage } from '../services/threadMetadata/uiLanguage';
import { PREST0_INC_ACCOUNT_ID } from '../config/internalAccess';
import GlobalFooter from '../components/GlobalFooter';
import KeepCase from '../components/common/KeepCase';
import SeoHead from '../components/SeoHead';
import {
  validateDeepLinkToken,
  type DeepLinkValidationResult,
} from '../services/globalTools/tools/browserDeepLinkValidator';
import {
  useRealtimeDesktopTarget,
  type RealtimeDesktopTarget,
} from '../hooks/useRealtimeDesktopTarget';
import { useRbac } from '../hooks/useRbac';
import AvatarBridge from '../services/avatarBridge/AvatarBridge';
import { WORKSTATION_BOOT_ESTIMATE, WORKSTATION_WAKE_ESTIMATE } from '../components/onboarding/JourneyStepper';
import { media } from '../styles/breakpoints';
import { bootPollDelayMs } from '../utils/bootPollCadence';
import { fetchWorkspaceBootStatus } from '../services/browserService/workspaceBootGate';
import { readBrowserServiceKeyFresh } from '../services/browserService/baseUrl';

// ── Which voice server the avatar page should open its socket to (avatar
// experience PR-B, 2026-09-20). The page cannot read ops/*; the boot-status
// endpoint answers `voice: {transport, url}` (the workstation, or the platform-
// hosted gateway once the account rides it), and the shell hands the URL to
// the page WITH the ID token. Cached per account so the token answer is never
// held up by a network round trip after the first; a fetch that has not come
// back in time simply yields no URL this round (= the workstation), never a
// stall of the voice sign-in, and the next round carries it.
const VOICE_TRANSPORT_TTL_MS = 5 * 60 * 1000;
const VOICE_TRANSPORT_WAIT_MS = 1200;
const voiceTransportCache = new Map<string, { url: string | null; at: number; pending: Promise<string | null> | null }>();
function fetchVoiceUrl(accountId: string): Promise<string | null> {
  const now = Date.now();
  const hit = voiceTransportCache.get(accountId);
  if (hit && hit.pending) return hit.pending;
  if (hit && now - hit.at < VOICE_TRANSPORT_TTL_MS) return Promise.resolve(hit.url);
  const pending = fetchWorkspaceBootStatus(accountId)
    .then((status) => {
      const voice = status?.voice;
      const url = voice && voice.transport === 'platform' && typeof voice.url === 'string' && voice.url ? voice.url : null;
      voiceTransportCache.set(accountId, { url, at: Date.now(), pending: null });
      return url;
    })
    .catch(() => {
      voiceTransportCache.set(accountId, { url: null, at: Date.now(), pending: null });
      return null;
    });
  voiceTransportCache.set(accountId, { url: hit?.url ?? null, at: hit?.at ?? 0, pending });
  return pending;
}
/** The URL if it is known within the wait; null otherwise (the fetch keeps going for next time). */
function voiceUrlWithin(accountId: string, waitMs: number): Promise<string | null> {
  return Promise.race([
    fetchVoiceUrl(accountId),
    new Promise<string | null>((resolve) => setTimeout(() => resolve(voiceTransportCache.get(accountId)?.url ?? null), waitMs)),
  ]);
}

// ─────────────────────────────────────────────────────────────────────────────
// /desktop now embeds the full Preston realtime avatar app from the user's
// account VM (or the internal Preston VM for Prest0 staff). This is an iframe
// integration, not a partial React rewrite — the realtime app owns the voice
// WebSocket, WebRTC lip-sync, mic capture, desktop launch APIs and noVNC view.
// See REALTIME_DESKTOP_NOTES.md for routing rules and smoke tests.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract origin (scheme+host) from a URL string. Used to derive the
 * AvatarBridge allowed-origins list from the avatar VM's baseUrl.
 *
 * Examples:
 *   'https://35-238-149-88.sslip.io'         → 'https://35-238-149-88.sslip.io'
 *   'https://35-238-149-88.sslip.io/foo'   → 'https://35-238-149-88.sslip.io'
 *   'not-a-url'                             → null
 */
/** Where the embedded page's machine key lives (2026-09-24): the shared internal key for the
 *  internal machine, otherwise the account's own vm record. */
function bsKeyTarget(
  target: Pick<RealtimeDesktopTarget, 'source' | 'accountId'>
): { kind: 'internal' } | { kind: 'account'; accountId: string } {
  if (target.source === 'internal') return { kind: 'internal' };
  return { kind: 'account', accountId: target.accountId ?? '' };
}

/** How often the shell hands the page its credential again (workstationTickets.ts DESKTOP_PAGE_REHAND_MS). */
const DESKTOP_PAGE_REHAND_MS = 4 * 60_000;

/**
 * What the embedded page reaches its workstation with (S1b-2c step 2,
 * 2026-09-25): a personal ticket that opens only this person's own desktops,
 * when the machine says it takes one; else the machine key, as before. The
 * ticket module is loaded on first use, so none of it joins the first download.
 */
async function credentialForPage(
  target: Pick<RealtimeDesktopTarget, 'source' | 'accountId' | 'baseUrl' | 'browserServiceToken'>
): Promise<string | null> {
  const accountId = target.source === 'internal' ? PREST0_INC_ACCOUNT_ID : target.accountId;
  if (target.baseUrl && accountId) {
    const host = `${target.baseUrl.replace(/\/+$/, '')}/browser-api`;
    const ticket = await import('../services/browserService/workstationTickets')
      .then((m) => m.ticketForDesktopPage(host, accountId))
      .catch(() => null);
    if (ticket) return ticket;
  }
  return (await readBrowserServiceKeyFresh(bsKeyTarget(target))) ?? target.browserServiceToken ?? null;
}

function toOrigin(url: string): string | null {
  try {
    const u = new URL(url);
    // URL.origin includes the port only when non-default (443 for https),
    // which is exactly the format window.postMessage's event.origin uses.
    return u.origin || null;
  } catch {
    return null;
  }
}

// ─── Layout ───────────────────────────────────────────────────────────────────

/* Full-bleed on purpose (2026-08-14 owner directive: the desktop must use
   the whole landscape) — the embed's desktop pane absorbs extra width by
   design, so no ultrawide cap.

   2026-08-28 (Miles, avatar-desktop redesign) — two things went:
   • `min-height: 100vh`. The site nav sits ABOVE this page in normal flow,
     so a full-viewport minimum here guaranteed the page was taller than the
     screen before any content existed.
   • the 6.5rem/7rem bottom padding. GlobalFooter is the last child, so that
     padding was never clearing the chat bar for the frame — it was ~110px of
     empty page UNDER the footer. The footer carries its own 5-7rem bottom
     clearance, which is what actually keeps the chat bar off it. */
const Page = styled.div`
  background: var(--brand-canvas);
  color: var(--brand-text);
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0.75rem 0;
  gap: 0.5rem;
  box-sizing: border-box;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  width: 100%;
  margin: 0 auto;

  ${media.tablet} {
    padding: 1rem 1rem 0;
    gap: 0.6rem;
  }

  ${media.laptop} {
    padding: 1rem 1.25rem 0;
  }
`;

/* 2026-08-28: the old header was a 62px block carrying "Prest0n" + "live
   desktop" above an embed whose own header already shows the wordmark and
   the tagline — the name was on screen four times. What survives is one
   slim row of things the embed CANNOT tell you: which workstation you are
   on, and the desktop-launch status. It keeps a fixed min-height so the
   status appearing mid-boot can never shove the live view down. */
const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  /* nowrap is load-bearing (2026-08-28 review): with wrap, a long status chip
     dropped to a second line on a phone and grew the row from 30px to ~62px
     mid-boot — shoving the live view down by exactly the amount this row
     exists to avoid. Both children shrink and ellipsize instead. */
  flex-wrap: nowrap;
  min-height: 30px;
  overflow: hidden;
`;

const SourcePill = styled.span<{ $source: 'internal' | 'account-vm' | 'none' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid
    ${({ $source }) =>
      $source === 'internal'
        ? 'var(--brand-gold)'
        : $source === 'account-vm'
        ? '#16a34a'
        : 'var(--brand-line)'};
  color: ${({ $source }) =>
    $source === 'internal'
      ? '#7a5f1f'
      : $source === 'account-vm'
      ? '#15803d'
      : '#777777'};
  background: ${({ $source }) =>
    $source === 'internal'
      ? 'rgba(198, 167, 94, 0.08)'
      : $source === 'account-vm'
      ? 'rgba(22, 163, 74, 0.06)'
      : 'rgba(0,0,0,0.02)'};
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
  flex-shrink: 0;
`;

/* Ellipsize the pill label rather than letting a long account id stretch or
   wrap the header row. */
const PillLabel = styled.span`
  /* inline-block, or overflow/ellipsis simply do not apply and a long account
     id stretches the pill and wraps the row — the exact failure this guards. */
  display: inline-block;
  max-width: 18ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PillDot = styled.span<{ $source: 'internal' | 'account-vm' | 'none' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $source }) =>
    $source === 'internal'
      ? 'var(--brand-gold)'
      : $source === 'account-vm'
      ? '#22c55e'
      : '#bbb'};
`;

/* 2026-08-28 (Miles, avatar-desktop redesign) — the frame is now exactly ONE
   screenful, measured.

   What it replaces: `min-height: max(720px, calc(100vh - 215px))`. That floor
   existed because the embed laid itself out on a fixed 720px canvas and
   transform-scaled by min(1, height/720), so a shorter frame shrank all of
   its text. The embed no longer scales — it is fluid — so the floor's reason
   is gone, and the floor itself was half the problem: on a tall window the
   frame stretched to 100vh-215px while the embed still painted only 720px,
   leaving a screen of dead background above the footer.

   $stageHeight is measured in the component (viewport height minus this
   element's document offset minus the fixed chat bar's allowance) because
   the site nav and the trial banner sit above this page in normal flow and
   their combined height is not a constant any calc() can know.

   $embeddedHeight is kept for compatibility: a VM still serving the PREVIOUS
   page reports a portrait content height, and until its 5-minute page sync
   lands we must still grow around it. The new page reports null. */
const Frame = styled.div<{ $embeddedHeight?: number | null; $stageHeight?: number | null }>`
  flex: none;
  /* notched phones: the chat allowance above is measured in CSS pixels from
     the layout viewport, which knows nothing about the home indicator */
  margin-bottom: env(safe-area-inset-bottom, 0px);
  height: ${({ $stageHeight }) => ($stageHeight ? `${$stageHeight}px` : 'min(78dvh, 760px)')};
  min-height: 320px;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--brand-line);
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  position: relative;

  ${media.tablet} {
    border-radius: 1rem;
  }

  ${({ $embeddedHeight }) =>
    $embeddedHeight
      ? `
    height: ${$embeddedHeight}px;
    min-height: ${$embeddedHeight}px;
    overflow: visible;
  `
      : ''}
`;

const StyledIframe = styled.iframe<{ $embeddedHeight?: number | null }>`
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 0;
  display: block;

  ${({ $embeddedHeight }) =>
    $embeddedHeight
      ? `
    flex: none;
    height: ${$embeddedHeight}px;
    min-height: ${$embeddedHeight}px;
  `
      : ''}
`;

// ─── Loading + Error states ───────────────────────────────────────────────────

/* min-height:0 + overflow-y:auto (2026-08-28 review): as a flex child of the
   now-fixed-height Frame, `flex:1` alone leaves min-height:auto, so a card
   taller than the frame overflowed a container with overflow:hidden — the
   stalled-boot card lost its "check again" button off the bottom edge with no
   scrollbar. Padding shrinks on short frames so scrolling is the last resort. */
const StateCard = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: clamp(1rem, 4vh, 3rem) 1.5rem;
  text-align: center;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--brand-line);
  border-top-color: var(--brand-gold);
  animation: ${spin} 0.9s linear infinite;
`;

/* ── Live desktop status strip (2026-08-09 desktop-reliability pass) ─────────
   The embedded avatar app reports its desktop-launch lifecycle up via
   postMessage ({source:'preston-realtime', type:'desktopStatus'}); this strip
   mirrors it in the shell so progress stays visible even when the desktop
   pane inside the iframe is scrolled out of view (portrait phones). It
   renders nothing when the desktop is 'ready' — the live view speaks for
   itself — and never overlays the avatar. */
/* 2026-08-28: this was a full-width strip ABOVE the frame, so every boot
   mounted and unmounted a ~54px block and shoved the live view down and back
   up while the user watched. It is now an inline chip on the status row,
   which reserves its height whether or not the chip is present. */
const DeskStatusStrip = styled.div<{ $failed?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  border: 1px solid ${({ $failed }) => ($failed ? '#EAD9D6' : '#ECEAE5')};
  background: #ffffff;
  font-size: 0.8rem;
  color: #555555;
  flex-shrink: 1;
  min-width: 0;
`;

const StripSpinner = styled(Spinner)`
  width: 13px;
  height: 13px;
  flex-shrink: 0;
`;

const DeskStatusText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeskStatusButton = styled.button`
  background: none;
  border: 1px solid var(--brand-gold);
  color: #7a5f1f;
  border-radius: 999px;
  padding: 0.25rem 0.8rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover { background: rgba(198, 167, 94, 0.08); }
`;

const StateTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--brand-text);
  text-wrap: balance;

  ${media.laptop} {
    font-size: 1.2rem;
  }
`;

const StateMessage = styled.p`
  margin: 0;
  max-width: 36rem;
  color: #666666;
  font-size: 0.9rem;
  line-height: 1.5;
  text-wrap: pretty;

  ${media.laptop} {
    font-size: 0.95rem;
    max-width: 40rem;
  }
`;

/* Boot/stall desktop treatment (2026-08-03 desktop-polish audit): one narrow
   centered paragraph in a 70vh full-width frame read as a broken mobile page.
   At ≥1024px the status pairs with a "ready right now" panel so the empty
   frame becomes information instead of void. Mobile keeps the compact card. */
const BootSplit = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* BootSplit owns the scroll; its children must not open a second one. */
  & > * {
    overflow: visible;
    flex: 0 0 auto;
  }

  ${media.laptop} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 400px);
    align-items: center;
    gap: 1.5rem;
    padding: 0 3rem;
  }
`;

const MeanwhilePanel = styled.aside`
  display: none;

  ${media.laptop} {
    display: block;
    background: #ffffff;
    border: 1px solid #ECEAE5;
    border-radius: 1rem;
    padding: 1.5rem 1.75rem;
    text-align: left;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);
  }
`;

const MeanwhileTitle = styled.h4`
  margin: 0 0 0.75rem;
  font-family: "Space Grotesk", "Inter", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--brand-text);
  text-transform: lowercase;
`;

const MeanwhileList = styled.ul`
  /* the items name Prest0n; the ancestor's lowercase idiom must not downcase
     a proper noun (2026-08-22 owner directive) */
  text-transform: none;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.6rem;
  font-size: 0.88rem;
  color: #555555;
  line-height: 1.5;
`;

const MeanwhileFoot = styled.p`
  margin: 0.9rem 0 0;
  font-size: 0.78rem;
  color: var(--brand-text-muted);
  line-height: 1.5;
  text-transform: lowercase;
`;

const RetryButton = styled.button`
  margin-top: 0.5rem;
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const NewTabLink = styled.a`
  font-size: 0.75rem;
  color: #777777;
  text-decoration: none;
  border-bottom: 1px dashed #cccccc;

  &:hover { color: var(--brand-text); }
`;

// ─── Expired-link UI (kept from prior /desktop deep-link guard) ──────────────

const ExpiredCard = styled.div`
  max-width: 480px;
  margin: 4rem auto;
  padding: 2rem;
  background: #ffffff;
  border: 1px solid var(--brand-line);
  border-radius: 0.75rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ExpiredTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0 0 0.75rem 0;
`;

const ExpiredCopy = styled.p`
  color: #666666;
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0 0 1.5rem 0;
`;

const ExpiredCta = styled.a`
  display: inline-block;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, var(--brand-gold), var(--brand-gold-hover));
  color: #ffffff;
  text-decoration: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

/* 2026-08-28: this panel and its four-item list were duplicated VERBATIM in
   the boot, stalled and asleep branches — three copies to keep in sync,
   differing only in the closing line. One component, one list. */
const READY_NOW: ReadonlyArray<string> = [
  '💬 chat with Prest0n — ask anything in the bar below',
  '🔍 deep research on any topic, market, or case',
  '📝 draft documents, letters, and proposals',
  '🌐 translate client communication instantly',
];

const ReadyNowPanel: React.FC<{ foot: React.ReactNode }> = ({ foot }) => (
  <MeanwhilePanel>
    <MeanwhileTitle>ready right now — no waiting</MeanwhileTitle>
    <MeanwhileList>
      {READY_NOW.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </MeanwhileList>
    <MeanwhileFoot>{foot}</MeanwhileFoot>
  </MeanwhilePanel>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function describeSource(target: RealtimeDesktopTarget): string {
  if (target.source === 'internal') return 'Internal Prest0n VM';
  if (target.source === 'account-vm' && target.provisioning) {
    return 'workstation booting…';
  }
  if (target.source === 'account-vm' && target.accountId) {
    // Uid-keyed fallback accounts have a raw Firebase UID as their account
    // id — meaningless (and mildly alarming) in the header. Friendly label
    // instead; named accounts (e.g. dash-separated firm ids) still show.
    if (/^[A-Za-z0-9]{20,}$/.test(target.accountId)) return 'your workspace';
    return `account: ${target.accountId}`;
  }
  return 'Not connected';
}

// Desktop-launch lifecycle states the embedded avatar app reports up via
// postMessage. Kept in lockstep with desktopStatus() in
// functions/src/prest0nVm/bootstrap/public/preston-realtime.html.
const DESK_STATUS_STATES = ['launching', 'restoring', 'retrying', 'ready', 'failed', 'queued'] as const;
type DeskStatusState = (typeof DESK_STATUS_STATES)[number];

// copy: desktop_status_chrome
const DESK_STATUS_COPY: Record<Exclude<DeskStatusState, 'ready'>, string> = {
  launching: 'starting your live desktop…',
  // 2026-09-05 (workstation plan PR-9): every desktop slot on the
  // workstation is a teammate's live session. The embed keeps retrying and
  // takes the first slot that frees; nothing else is blocked meanwhile.
  queued: "someone's using this desktop right now — you're next. chat and everything else work in the meantime.",
  // 2026-09-03: also what the embed's live-view watchdog reports when it is
  // repointing a dropped viewer after a sleep — and it only reports it once
  // a reconnect has taken longer than 1.5s, so this chip means "genuinely
  // taking a moment", never the sub-second recovery of a reopened laptop.
  restoring: 'reconnecting your desktop…',
  retrying: 'the desktop is taking a moment — retrying automatically…',
  failed: "the desktop couldn't start — chat and everything else still work.",
};

// ─── Component ────────────────────────────────────────────────────────────────

const AgentActivity: React.FC = () => {
  // Post-merge wiring (PRE-212): if the URL carries a `?token=` (or `?t=`)
  // query param, validate it against the validateBrowserDeepLink Cloud
  // Function before rendering. On invalid/expired/missing-key, render the
  // "link expired" card with a CTA back to /desktop. If no token is
  // present, render the realtime desktop iframe.
  const [tokenStatus, setTokenStatus] = useState<
    'pending' | 'valid' | 'expired' | 'no-token'
  >('pending');

  // ── Per-session service-base override (2026-08-18) ────────────────────
  // Sign-in desktop sessions can live on the user's ACCOUNT VM instead of
  // the platform-shared service; their deep links carry `svc=<base>`. Set
  // the runtime override (which every browser-service base resolver already
  // honors) BEFORE the token gate flips and the live view mounts, so
  // frames/keepalive/noVNC all target the VM that actually hosts the
  // session. STRICTLY validated: https, one of OUR dashed-sslip VM hosts,
  // and a known service path — anything else is ignored, so the param can
  // never point this page (and its auth token) at a foreign host.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const svc = (params.get('svc') || '').trim();
      if (!svc) return;
      const u = new URL(svc);
      const hostOk = u.protocol === 'https:' && /^(?:\d{1,3}-){3}\d{1,3}\.sslip\.io$/.test(u.hostname);
      const pathOk = ['', '/', '/browser', '/browser-api'].includes(u.pathname.replace(/\/+$/, '') || '');
      if (hostOk && pathOk && !u.username && !u.password) {
        (window as unknown as { PREST0_BROWSER_SERVICE_BASE_URL?: string })
          .PREST0_BROWSER_SERVICE_BASE_URL = `${u.origin}${u.pathname.replace(/\/+$/, '')}`;
      }
    } catch { /* malformed param — shared base applies */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(search);
    const token = params.get('token') ?? params.get('t');
    if (!token) {
      setTokenStatus('no-token');
      return;
    }
    void (async () => {
      const result: DeepLinkValidationResult = await validateDeepLinkToken(token);
      if (cancelled) return;
      setTokenStatus(result.valid ? 'valid' : 'expired');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const target = useRealtimeDesktopTarget();

  // ── "Show me on the desktop page" (2026-09-20, P6b) ───────────────────
  // A computer run's card, the voice hand-off and the signed deep link all
  // name the desktop SESSION the person should be looking at. The deep-link
  // minter had put `?session=` in the URL since the day it shipped and this
  // page threw it away. Validated to the browser-service's own id charset and
  // handed to the embed on its URL, where the page attaches to it at boot
  // instead of launching its home page over it — and REFUSES it there if the
  // session belongs to someone else (the page verifies the owner). Absent ⇒
  // the URL, and the page, are byte-identical to before.
  const requestedSessionId = useMemo(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('session') || '';
      return /^desktop-[a-z0-9-]{6,64}$/i.test(raw) ? raw : null;
    } catch {
      return null;
    }
  }, []);
  const embedSrc = useMemo(() => {
    if (!target.targetUrl) return null;
    if (!requestedSessionId) return target.targetUrl;
    const joiner = target.targetUrl.includes('?') ? '&' : '?';
    return `${target.targetUrl}${joiner}session=${encodeURIComponent(requestedSessionId)}`;
  }, [target.targetUrl, requestedSessionId]);


  // Bump key on retry to force iframe reload.
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // L0.4 (2026-09-08): the iframe mounts only once the target has resolved (loading /
  // provisioning / queue / hold / stall gates below), while the bridge registration effect used
  // to key on baseUrl + accountId + iframeKey — so an iframe that appeared AFTER those settled
  // was never registered and the allowlist stayed empty. The element itself is now a dependency.
  const [iframeEl, setIframeEl] = useState<HTMLIFrameElement | null>(null);
  const bindIframe = useCallback((el: HTMLIFrameElement | null) => {
    iframeRef.current = el;
    setIframeEl(el);
  }, []);
  const postShellHello = useCallback(() => {
    const origin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    const frameWindow = iframeRef.current?.contentWindow;
    if (!origin || !frameWindow) return;
    try {
      frameWindow.postMessage({ source: 'prest0-shell', type: 'hello' }, origin);
    } catch { /* ignore */ }
  }, [target.baseUrl]);

  // ─────────────────────────────────────────────────────────────────────
  // One-screen stage height (2026-08-28, avatar-desktop redesign).
  //
  // The frame should be exactly the screen minus what is above it and the
  // fixed chat bar below it — no more (dead background above the footer),
  // no less (a cramped live desktop). No calc() can express that: the site
  // nav and the trial banner are IN NORMAL FLOW above this page and their
  // combined height varies by account state, and the docked chat pane
  // narrows the content shell without changing the viewport. So measure.
  //
  // getBoundingClientRect().top is viewport-relative, so scrollY is added
  // back to get a scroll-independent document offset — the value must not
  // change as the user scrolls to the footer.
  // ─────────────────────────────────────────────────────────────────────
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [stageHeight, setStageHeight] = useState<number | null>(null);
  useEffect(() => {
    const measure = () => {
      const el = frameRef.current;
      if (!el) return;
      const offsetFromDocTop = el.getBoundingClientRect().top + window.scrollY;
      // LAYOUT viewport, deliberately — never visualViewport. offsetFromDocTop
      // above is a layout-viewport measurement, so mixing in a visual-viewport
      // height makes the two disagree exactly when they diverge: an open mobile
      // keyboard or a hidden URL bar would collapse the live desktop to its
      // floor and jitter it on every scroll (2026-08-28 review).
      const vh = window.innerHeight;
      // Clearance for the fixed chat bar. Floor is the platform's own numbers
      // — --prompt-bar-gap (1.5rem) + --prompt-bar-height (56px) in
      // GlobalStyles, plus slack because the bar renders nearer 68px — and the
      // safe-area inset is added in CSS by the Frame's margin-bottom rather
      // than guessed here. Kept as tight as that allows on purpose: this height
      // is what the embed reads to choose its layout, so an over-generous
      // allowance on a 768px laptop pushed it into the layout meant for phones
      // (2026-08-28 review).
      const chatAllowance = Math.min(116, Math.max(96, Math.round(vh * 0.11)));
      const next = Math.round(vh - offsetFromDocTop - chatAllowance);
      // Never below a usable minimum: a very short window scrolls instead of
      // collapsing the live desktop to a sliver.
      setStageHeight((prev) => {
        const clamped = Math.max(320, next);
        // Ignore sub-pixel churn so a resize storm can't loop us.
        return prev !== null && Math.abs(prev - clamped) < 2 ? prev : clamped;
      });
    };
    measure();
    // The banner and nav mount after us; re-measure once they have settled.
    const t1 = setTimeout(measure, 120);
    const t2 = setTimeout(measure, 600);
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('orientationchange', measure);
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(measure);
      ro.observe(document.documentElement);
    } catch { /* no ResizeObserver — the listeners above still cover resizes */ }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      ro?.disconnect();
    };
  }, []);

  // Embedded-portrait content height reported by the avatar page via
  // postMessage. Null = desktop/landscape (fixed-height box). A number =
  // grow the box to that height so the whole outer page scrolls as one.
  const [embeddedHeight, setEmbeddedHeight] = useState<number | null>(null);

  // Desktop-launch lifecycle reported by the embedded avatar app. Null =
  // nothing to show (no report yet, or the desktop is ready).
  const [deskStatus, setDeskStatus] = useState<Exclude<DeskStatusState, 'ready'> | null>(null);

  // ─────────────────────────────────────────────────────────────────────
  // Avatar/Platform Bridge integration (2026-05-23).
  //
  // Register the avatar iframe with the bridge singleton and set the
  // allowed origins for incoming bridge requests to the current user's
  // avatar VM origin. Without this registration the iframe can send
  // bridge requests but responses can't come back (no target window).
  //
  // Per-account scope comes from `target.baseUrl` (resolved by
  // useRealtimeDesktopTarget against the user's Firestore account doc).
  // Internal Prest0 users get the internal VM origin (DEFAULT_INTERNAL_BASE
  // in useRealtimeDesktopTarget). Account users get their own VM URL.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = iframeEl;
    if (!el) {
      AvatarBridge.registerIframe(null);
      AvatarBridge.setAllowedOrigins([]);
      return;
    }
    const origin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    AvatarBridge.registerIframe(el, target.accountId || undefined);
    if (origin) {
      AvatarBridge.setAllowedOrigins([origin]);
      // L0.4: identify ourselves to the page, pinned to the VM origin. The page pins the shell's
      // origin from the first parent message on an allowlisted origin and, until then, can only
      // guess the fixed production hosts (our index.html sends no referrer) — so a preview-channel
      // or localhost shell is found through this hello. Posted again from onLoad in case the
      // frame has not loaded yet.
      try {
        el.contentWindow?.postMessage({ source: 'prest0-shell', type: 'hello' }, origin);
      } catch { /* not loaded yet — onLoad posts again */ }
    } else {
      AvatarBridge.setAllowedOrigins([]);
    }
    return () => {
      AvatarBridge.registerIframe(null);
      AvatarBridge.setAllowedOrigins([]);
    };
  }, [iframeEl, target.baseUrl, target.accountId]);

  // ─────────────────────────────────────────────────────────────────────
  // Embedded-portrait height bridge (2026-07-01).
  //
  // On portrait phones the avatar page stacks the avatar + live desktop
  // taller than one screen and reports its real content height up to us.
  // We grow the Frame/iframe to that height (and drop the overflow clip)
  // so the whole /desktop page scrolls as one, instead of the tall child
  // panning inside a fixed-height box. Origin is validated against the
  // resolved avatar VM origin (same source the AvatarBridge trusts).
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const allowedOrigin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    if (!allowedOrigin) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if ((data as { source?: unknown }).source !== 'preston-realtime') return;
      const type = (data as { type?: unknown }).type;

      // Browser-service auth handshake (2026-08-20 401-wall fix): the VM
      // page cannot read Firestore, so it asks the shell for the per-VM
      // /browser-api bearer and we answer over postMessage, pinned to the
      // VM origin. Never placed in the iframe URL.
      if (type === 'requestBsAuth') {
        // Decided fresh on every ask (2026-09-24; tickets 2026-09-25): the page asks at load,
        // while it waits to launch, and again after the machine refuses a call. It gets a
        // personal ticket where the machine takes one, else the key as it is now.
        void credentialForPage(target).then((token) => {
          const frameWindow = iframeRef.current?.contentWindow;
          if (token && frameWindow) {
            frameWindow.postMessage(
              { source: 'prest0-shell', type: 'bsAuth', token },
              allowedOrigin,
            );
          }
        });
        return;
      }

      // Voice socket identity (L0.2 safe relaunch, 2026-09-05): the VM's voice
      // server refuses anonymous sockets. The page asks; we answer with the
      // signed-in user's Firebase ID token (plus uid, account, UI language and
      // the bs bearer as a second factor), pinned to the VM origin — never in
      // the iframe URL. The server asks again five minutes before expiry.
      if (type === 'requestVoiceAuth') {
        const fbUser = getAuth().currentUser;
        const frameWindow = iframeRef.current?.contentWindow;
        // The internal target carries the label 'prest0-internal' for logging; the voice
        // server checks membership against its own account, so send the real one.
        const voiceAccountId = target.source === 'internal' ? PREST0_INC_ACCOUNT_ID : target.accountId;
        const forceRefresh = (event.data as { refresh?: unknown } | null)?.refresh === true;
        if (fbUser && frameWindow) {
          fbUser
            .getIdTokenResult(forceRefresh)
            .then(async ({ token: idToken, expirationTime }) => {
              const realExpiry = Date.parse(expirationTime);
              // Where the voice socket opens (PR-B): the platform gateway's URL when
              // this account rides it, else null = the workstation, as before.
              const voiceUrl = voiceAccountId ? await voiceUrlWithin(voiceAccountId, VOICE_TRANSPORT_WAIT_MS) : null;
              // The page's credential as it is now: a personal ticket where the machine takes
              // one, else the key (2026-09-24/25). The page adopts whatever bsToken arrives
              // here, and the voice server takes a ticket as its second factor only when it
              // names this very person.
              const bsToken = await credentialForPage(target);
              frameWindow.postMessage(
                {
                  source: 'prest0-shell',
                  type: 'voiceAuth',
                  idToken,
                  uid: fbUser.uid,
                  accountId: voiceAccountId,
                  language: getUserUiLanguage(),
                  bsToken,
                  expiresAt: Number.isFinite(realExpiry) ? realExpiry : Date.now() + 55 * 60 * 1000,
                  voiceUrl,
                },
                allowedOrigin,
              );
            })
            .catch(() => {
              /* not signed in — the server decides */
            });
        }
        return;
      }

      // Desktop-launch lifecycle → shell status strip (2026-08-09).
      if (type === 'desktopStatus') {
        const raw = (data as { state?: unknown }).state;
        if (
          typeof raw === 'string' &&
          (DESK_STATUS_STATES as readonly string[]).includes(raw)
        ) {
          setDeskStatus(raw === 'ready' ? null : (raw as Exclude<DeskStatusState, 'ready'>));
        }
        return;
      }

      if (type !== 'contentHeight') return;
      const raw = (data as { height?: unknown }).height;
      if (typeof raw === 'number' && isFinite(raw) && raw > 0) {
        // Clamp to a sane range to guard against runaway values.
        setEmbeddedHeight(Math.min(Math.max(Math.round(raw), 320), 20000));
      } else {
        setEmbeddedHeight(null);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [target.baseUrl, target.browserServiceToken, target.accountId, target.source]);

  // Hand the page its credential again while it is open (S1b-2c step 2): a ticket lives 15
  // minutes and the page holds only what it is handed, so a fresh one arrives before the old
  // one runs out. The page adopts whatever arrives, the machine key included.
  useEffect(() => {
    const allowedOrigin = target.baseUrl ? toOrigin(target.baseUrl) : null;
    if (!allowedOrigin) return;
    const timer = setInterval(() => {
      void credentialForPage(target).then((token) => {
        const frameWindow = iframeRef.current?.contentWindow;
        if (token && frameWindow) {
          frameWindow.postMessage({ source: 'prest0-shell', type: 'bsAuth', token }, allowedOrigin);
        }
      });
    }, DESKTOP_PAGE_REHAND_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.baseUrl, target.browserServiceToken, target.accountId, target.source]);

  // Reset any embedded height + reported desktop status when the iframe
  // reloads or leaves the ready state, so a fresh load starts clean.
  useEffect(() => {
    setEmbeddedHeight(null);
    setDeskStatus(null);
  }, [iframeKey, target.targetUrl, target.loading, target.error]);

  // "check again" (2026-08-19, stuck-boot RCA): the old handler was a bare
  // window.location.reload() — a guaranteed NO-OP for every stuck-boot cause
  // (the reload just re-entered the same poll loop). Now it actually acts:
  // an immediate getWorkspaceBootStatus round (which is also the promoter
  // AND runs host discovery server-side) plus a target re-resolve. The
  // iframe key bump keeps the retry semantics for embed failures.
  const bootPollRef = useRef<null | (() => Promise<void>)>(null);
  const handleRetry = () => {
    setIframeKey((k) => k + 1);
    if (bootPollRef.current) {
      void bootPollRef.current();
      target.refresh();
      return;
    }
    // No poller armed (plain embed error) — the soft reload remains the
    // cheapest full re-resolve.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Idle-lifecycle wake (2026-08-18): the asleep-card CTA and the /desktop
  // predictive warm both call the wakeWorkspaceVm function — the same wake
  // seam OS-capability chat turns use — then re-resolve the target so the
  // waking card + boot poller take over.
  // ─────────────────────────────────────────────────────────────────────
  const [wakeBusy, setWakeBusy] = useState(false);
  const requestWake = async (source: 'desktop' | 'button') => {
    if (wakeBusy) return;
    setWakeBusy(true);
    try {
      const fbUser = getAuth().currentUser;
      if (!fbUser) return;
      const token = await fbUser.getIdToken();
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
      await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/wakeWorkspaceVm`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ source }),
        },
      );
      target.refresh();
    } catch {
      /* the asleep card stays up; the user can tap again */
    } finally {
      setWakeBusy(false);
    }
  };

  // Predictive warm: opening /desktop while the workstation sleeps is
  // unambiguous intent — wake it without a click (warmWorkspace latch
  // pattern: once per tab per account; billing sleeps never auto-wake).
  useEffect(() => {
    if (target.lifecycle !== 'asleep' || target.stopReason === 'billing') return;
    if (!target.accountId) return;
    const latchKey = `prest0_desktop_wake_fired_${target.accountId}`;
    try {
      if (sessionStorage.getItem(latchKey)) return;
      sessionStorage.setItem(latchKey, '1');
    } catch { /* storage unavailable — still fire once per mount */ }
    void requestWake('desktop');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.lifecycle, target.stopReason, target.accountId]);

  // ─────────────────────────────────────────────────────────────────────
  // Instant-workspace boot poller (2026-08-02, owner directive).
  //
  // While the account VM reports status 'provisioning' — or a wake is in
  // flight (lifecycle 'waking', 2026-08-18) — poll the
  // getWorkspaceBootStatus function (which is also the health-gated
  // PROMOTER — it flips the routing docs to 'active' the moment both the
  // adapter and the avatar page pass probes). On 'ready', re-resolve the
  // target so the iframe mounts. Server-side probing avoids the CORS wall
  // a direct browser /healthz fetch would hit.
  // ─────────────────────────────────────────────────────────────────────
  const [bootElapsedMin, setBootElapsedMin] = useState(0);
  // Server-authoritative stall flag (getWorkspaceBootStatus → bootStatus.ts
  // WORKSTATION_STALL_MINUTES) so the client and the sweep agree on when a
  // boot has gone wrong rather than each guessing from a local clock.
  const [bootStalled, setBootStalled] = useState(false);
  // Machine-readable stuck-boot diagnosis (2026-08-19): the server's
  // host-discovery / probe outcome, when it has one — drives card copy that
  // says what is actually wrong instead of a generic "taking longer".
  const [bootStallReason, setBootStallReason] = useState<string | null>(null);
  // Adapter is up but browser-service (the desktop renderer) isn't yet —
  // the boot card shows "warming up" instead of mounting a doomed iframe.
  const [desktopWarming, setDesktopWarming] = useState(false);
  // Workstation queue (2026-09-05, workstation plan PR-10): when every desktop
  // slot is a teammate's live session, the chat lane queues the viewer's task
  // (accounts/{id}/workstationQueue, server-written, member-readable) and the
  // 1-minute sweep resumes it as slots free. The tile shows the viewer's OWN
  // ticket — "you're #n" from the position the sweep keeps on the doc, or
  // "your turn" once summoned — instead of an iframe that can only wait.
  // Nothing here decides; a read failure simply shows no card.
  // scaling (PR-13b): the ticket waits on another of the organization's
  // workstations coming up — a wake (~90 s) or a new member (~10 min).
  // Workstation pool strip (2026-09-07, workstation plan PR-13c): for a holder
  // of org.observability.workstations.view (owner by derivation, admin by a
  // deliberate grant), the status row shows the organization's OWN pool —
  // "2 workstations · 1 in use · 1 waking" — from the boot-status endpoint's
  // capacity block, which carries the counts for holders only (the server
  // decides; the client-side key check just spares the poll for everyone
  // else). Counts only; never who is using a machine. Facts, nothing decided.
  const { hasPermission: rbacHas } = useRbac();
  const canSeePool = rbacHas('org.observability.workstations.view');
  const [poolStrip, setPoolStrip] = useState<{ total: number; up: number; waking: number } | null>(null);
  useEffect(() => {
    setPoolStrip(null);
    if (!canSeePool || target.source !== 'account-vm' || !target.accountId || target.lifecycle !== 'active') return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        const resp = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/getWorkspaceBootStatus?desktop=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok || cancelled) return;
        const data = await resp.json().catch(() => ({}));
        const cap = data?.capacity && typeof data.capacity === 'object' ? (data.capacity as Record<string, unknown>) : null;
        const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
        const total = cap ? n(cap.membersTotal) : null;
        if (cancelled) return;
        if (total === null || total < 2) { setPoolStrip(null); return; }
        // Facts only: how many are on record, how many are up (serving), how
        // many are waking / being set up. Nothing here is inferred.
        setPoolStrip({ total, up: n(cap!.membersReady) ?? 0, waking: n(cap!.membersWaking) ?? 0 });
      } catch {
        /* a probe blip never hides or invents a strip */
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [canSeePool, target.accountId, target.source, target.lifecycle]);
  /**
   * A SIGN-IN OWED IS A BUTTON, NOT A DEAD END (Miles, 2026-09-20).
   *
   * The incident this closes: a customer was told to come to this page to
   * finish signing into eFax, arrived, and found nothing about eFax on it —
   * because the destination, the pilot, the pointing overlay, the paste
   * bridge and the finish row all live in the connect surface, which only the
   * Integrations tile and a chat card could open. P1 stopped Prest0n sending
   * people here for a sign-in; this makes the page useful to everyone who
   * arrives anyway — after a voice hand-off, from the workspace switcher, or
   * on an old link.
   *
   * ONE strip, never a list: the guidance budget is one directive on screen,
   * and it rides the StatusRow beside the pool and desktop chips rather than
   * inside the Frame, so it cannot cover the live desktop or move the stage
   * (npm run gate:desktop-layout pins that geometry at 14 viewports).
   *
   * Read-only and best-effort: a failed list leaves the strip absent, which
   * is exactly the page as it was before this shipped.
   */
  const [signInOwed, setSignInOwed] = useState<PendingBrowserLaneSignIn | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { listCustomConnections } = await import('../services/composio/ComposioClient');
        const { pendingBrowserLaneSignIns } = await import('../services/composio/browserLaneRoster');
        const owed = pendingBrowserLaneSignIns(await listCustomConnections());
        if (!cancelled) setSignInOwed(owed[0] ?? null);
      } catch {
        /* best-effort — no strip is the pre-2026-09-20 page */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * The click is the SAME door the Integrations tile opens — resumeCustomSignIn
   * starts the thread that picks the journey back up AND opens the surface.
   * Reusing it is the point: two ways to resume a sign-in is how they drift.
   */
  const handleResumeSignIn = useCallback(() => {
    const owed = signInOwed;
    if (!owed) return;
    void import('../services/composio/connectCustomFlow')
      .then((m) => m.resumeCustomSignIn({
        name: owed.name,
        slug: owed.slug,
        signInUrl: owed.signInUrl,
        scope: owed.scope,
      }))
      .catch(() => { /* the surface reports its own failures */ });
  }, [signInOwed]);

  const [queueTicket, setQueueTicket] = useState<{ status: 'queued' | 'summoned'; position: number | null; etaSeconds: number | null; scaling: { action: 'waking' | 'minting'; etaSeconds: number | null } | null } | null>(null);
  useEffect(() => {
    setQueueTicket(null);
    if (target.source !== 'account-vm' || !target.accountId) return;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    let unsub: (() => void) | null = null;
    try {
      const db = getFirestore(getApp());
      const q = query(
        collection(db, ACCOUNT_FIRESTORE_PREFIX, target.accountId, 'workstationQueue'),
        where('actingUid', '==', uid),
      );
      unsub = onSnapshot(
        q,
        (snap) => {
          let mine: { status: 'queued' | 'summoned'; position: number | null; etaSeconds: number | null; scaling: { action: 'waking' | 'minting'; etaSeconds: number | null } | null } | null = null;
          snap.forEach((d) => {
            const t = d.data() as Record<string, unknown>;
            if (t.status !== 'queued' && t.status !== 'summoned') return;
            if (mine && mine.status === 'summoned') return; // a summoned ticket wins: the slot is already ours
            const sc = t.scaling && typeof t.scaling === 'object' ? (t.scaling as Record<string, unknown>) : null;
            mine = {
              status: t.status,
              position: typeof t.position === 'number' ? t.position : null,
              etaSeconds: typeof t.etaSeconds === 'number' ? t.etaSeconds : null,
              scaling: sc && (sc.action === 'waking' || sc.action === 'minting')
                ? { action: sc.action, etaSeconds: typeof sc.etaSeconds === 'number' ? sc.etaSeconds : null }
                : null,
            };
          });
          setQueueTicket(mine);
        },
        () => setQueueTicket(null),
      );
    } catch {
      setQueueTicket(null);
    }
    return () => { if (unsub) unsub(); };
  }, [target.accountId, target.source]);

  // Arrival hold (2026-08-20 eager-org RCA): with eager provisioning, the
  // account is usually promoted to 'active' while the user is still in
  // onboarding — so the provisioning-path desktop hold below never runs and
  // the iframe used to mount straight into a cold/desktop-less VM page. On
  // every arrival at an active account-vm target, hold the iframe behind the
  // warming card until the server's desktop probe answers, failing open 90s
  // from ARRIVAL (never from the long-spent provisioning kickoff).
  const [desktopHold, setDesktopHold] = useState(false);
  useEffect(() => {
    if (
      target.provisioning ||
      target.lifecycle !== 'active' ||
      target.source !== 'account-vm' ||
      !target.targetUrl
    ) {
      setDesktopHold(false);
      return;
    }
    let cancelled = false;
    setDesktopHold(true);
    const arrivalMs = Date.now();
    const release = () => { if (!cancelled) setDesktopHold(false); };
    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - arrivalMs >= 90_000) { release(); return; }
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) { release(); return; }
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        const resp = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/getWorkspaceBootStatus?desktop=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) { release(); return; } // fail-open: probe trouble never blocks the desktop
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.desktopReady === false) return; // still warming — hold and re-poll
        release();
      } catch {
        release();
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 5_000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.targetUrl, target.provisioning, target.lifecycle, target.source]);
  useEffect(() => {
    // Re-arm cleanly: a different account (or a fresh boot) must never
    // inherit the previous one's stalled verdict.
    setBootStalled(false);
    setBootStallReason(null);
    setDesktopWarming(false);
    if (!target.provisioning && target.lifecycle !== 'waking' && target.lifecycle !== 'rebuilding') return;
    let cancelled = false;
    const startedMs = target.provisioningStartedAtMs ?? Date.now();
    // Desktop-warming fail-open is measured from when the user is WATCHING,
    // not from the eager kickoff at profile save (2026-08-20 fix — the old
    // 4-minute window was usually spent before anyone arrived).
    const warmupDeadlineMs = Date.now() + 4 * 60_000;

    const tickElapsed = () => {
      if (cancelled) return;
      setBootElapsedMin(Math.max(0, Math.floor((Date.now() - startedMs) / 60000)));
    };
    tickElapsed();
    const elapsedInterval = setInterval(tickElapsed, 30_000);

    const poll = async () => {
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        const resp = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/getWorkspaceBootStatus?desktop=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) return;
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.state === 'ready') {
          setBootStalled(false);
          // Desktop hold (2026-08-19 terminal-card RCA): "ready" proves the
          // adapter + avatar page, not the browser-service that RENDERS the
          // desktop. Mounting the iframe early burned the VM page's retry
          // ladder against a cold /browser-api and dead-ended on "desktop is
          // taking a moment". Keep the honest boot card up until the server's
          // desktop probe answers — fail-open after 4 minutes so a broken
          // health endpoint can never hold the desktop hostage.
          if (data.desktopReady === false && Date.now() < warmupDeadlineMs) {
            setDesktopWarming(true);
            return; // keep polling; target.refresh() would mount the iframe
          }
          setDesktopWarming(false);
          target.refresh();
          return;
        }
        if (data?.state === 'provisioning' || data?.state === 'waking') {
          setBootStalled(data.stalled === true);
          setBootStallReason(typeof data.stallReason === 'string' ? data.stallReason : null);
        }
        if (data?.state === 'stopped' && (target.lifecycle === 'waking' || target.lifecycle === 'rebuilding')) {
          // The wake fell back to stopped server-side — re-resolve so the
          // asleep card returns instead of an eternal waking spinner.
          target.refresh();
        }
      } catch {
        /* transient — next poll retries */
      }
    };
    // Adaptive cadence (2026-09-09) — see utils/bootPollCadence. A flat 20 s
    // interval meant a machine that finished booting sat behind a "still
    // setting up" card for up to twenty more seconds, which on a first visit
    // is indistinguishable from the boot itself being slow. A self-scheduling
    // timeout instead of setInterval, so the gap can shorten near the moment
    // somebody actually arrived and widen again once the wait is unattended.
    const watchStartedMs = Date.now();
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleNextPoll = () => {
      if (cancelled) return;
      pollTimer = setTimeout(() => {
        void poll().finally(scheduleNextPoll);
      }, bootPollDelayMs(Date.now() - watchStartedMs));
    };
    void poll().finally(scheduleNextPoll);
    // Expose the live poll to "check again" (2026-08-19).
    bootPollRef.current = poll;

    return () => {
      cancelled = true;
      bootPollRef.current = null;
      if (pollTimer) clearTimeout(pollTimer);
      clearInterval(elapsedInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.provisioning, target.provisioningStartedAtMs, target.lifecycle]);

  // ─────────────────────────────────────────────────────────────────────
  // Returning-tab re-resolve (2026-09-03, Miles: the desktop "times out …
  // and goes to a dead screen even if the user leaves and goes to another
  // tab and comes back").
  //
  // The embed owns reconnecting its OWN live view (see the watchdog in
  // preston-realtime.html) — but it cannot fix the one failure that lives
  // out here: while the tab was away the workstation may have slept and
  // woken on a NEW address, so the iframe's src points at a host that no
  // longer answers and no amount of in-page reconnecting can help. Only a
  // re-resolve against the account doc can, and it is one read.
  //
  // Gated on a real absence (60s+) so tabbing away for a moment costs
  // nothing, and deliberately unconditional otherwise: a re-resolve that
  // returns the same target is a no-op, and guessing whether the address
  // changed is exactly the guess that leaves people on a dead frame.
  // ─────────────────────────────────────────────────────────────────────
  const hiddenSinceRef = useRef<number | null>(null);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now();
        return;
      }
      const away = hiddenSinceRef.current ? Date.now() - hiddenSinceRef.current : 0;
      hiddenSinceRef.current = null;
      if (away >= 60_000) target.refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Iframe load watchdog: a refused/failed cross-origin load never fires
  // onLoad and is invisible to React — this converts silence into a
  // visible retry state instead of a dead frame in a pretty card.
  // ─────────────────────────────────────────────────────────────────────
  const [frameStalled, setFrameStalled] = useState(false);
  const frameLoadedRef = useRef(false);
  useEffect(() => {
    if (!target.targetUrl) return;
    frameLoadedRef.current = false;
    setFrameStalled(false);
    const timer = setTimeout(() => {
      if (!frameLoadedRef.current) setFrameStalled(true);
    }, 20_000);
    return () => clearTimeout(timer);
  }, [target.targetUrl, iframeKey]);

  const handleFrameRetry = () => {
    setFrameStalled(false);
    setIframeKey((k) => k + 1);
    target.refresh();
  };

  if (tokenStatus === 'expired') {
    return (
      <>
        <SeoHead
          tabTitle="desktop"
          title="Desktop | prest0"
          description="Your Prest0n desktop — watch and interact in real-time"
        />
        <Page>
          <ExpiredCard>
            <ExpiredTitle>this session link expired</ExpiredTitle>
            <ExpiredCopy>
              open a fresh one from your chat and you'll pick straight back up.
            </ExpiredCopy>
            <ExpiredCta href="/desktop">back to the desktop</ExpiredCta>
          </ExpiredCard>
          <GlobalFooter />
        </Page>
      </>
    );
  }

  return (
    <>
      <SeoHead
        tabTitle="desktop"
        title="Desktop | prest0"
        description="Your Prest0n desktop — watch and interact in real time"
      />
      <Page>
        {/* 2026-08-13 (Miles): he's not "Prest0n avatar" or "Prest0n Desktop"
            — he's just Prest0n; keep header text minimal. 2026-08-28 takes
            that to its conclusion: the embed's own header already shows the
            wordmark, so this row carries only what the embed cannot know —
            which workstation you are on, and how its desktop is coming up.
            Both live on ONE fixed-height row so neither can shove the live
            view around while it boots. */}
        <StatusRow>
          <SourcePill $source={target.source}>
            <PillDot $source={target.source} />
            <PillLabel>{describeSource(target)}</PillLabel>
          </SourcePill>
          {poolStrip && (
            <DeskStatusStrip data-testid="workstation-pool-strip" title="your organization's workstations — counts only">
              <DeskStatusText>
                {`${poolStrip.total} workstations · ${poolStrip.up} up${poolStrip.waking > 0 ? ` · ${poolStrip.waking} waking` : ''}`}
              </DeskStatusText>
            </DeskStatusStrip>
          )}
          {deskStatus && (
            <DeskStatusStrip $failed={deskStatus === 'failed'} data-testid="desk-status-strip">
              {deskStatus !== 'failed' && <StripSpinner />}
              <DeskStatusText>{DESK_STATUS_COPY[deskStatus]}</DeskStatusText>
              {deskStatus === 'failed' && (
                <DeskStatusButton onClick={handleFrameRetry}>try again</DeskStatusButton>
              )}
            </DeskStatusStrip>
          )}
          {signInOwed && (
            <DeskStatusStrip data-testid="signin-owed-strip">
              <DeskStatusText>
                {signInOwed.status === 'signin-lost'
                  ? `${signInOwed.name} sign-in expired`
                  : `${signInOwed.name} sign-in unfinished`}
              </DeskStatusText>
              <DeskStatusButton data-testid="signin-owed-resume" onClick={handleResumeSignIn}>
                {`sign in to ${signInOwed.name}`}
              </DeskStatusButton>
            </DeskStatusStrip>
          )}
          {/* "Open in new tab" demoted out of the header (2026-08-09): the
              href is the raw VM URL + identity params — useful only as a
              fallback, so it now lives in the not-responding card below. */}
        </StatusRow>

        <Frame ref={frameRef} $embeddedHeight={embeddedHeight} $stageHeight={stageHeight}>
          {target.loading && (
            <StateCard>
              <Spinner />
              <StateTitle>getting your desktop ready…</StateTitle>
              <StateMessage>connecting you to Prest0n.</StateMessage>
            </StateCard>
          )}

          {!target.loading && target.error && (
            <StateCard>
              <StateTitle>your desktop isn't available right now</StateTitle>
              <StateMessage>{target.error}</StateMessage>
              <RetryButton onClick={handleRetry}>try again</RetryButton>
            </StateCard>
          )}

          {!target.loading && !target.error && target.provisioning && !bootStalled && (
            <BootSplit>
              <StateCard data-testid="workspace-boot-card">
                <Spinner />
                <StateTitle>{desktopWarming ? 'your desktop is warming up' : 'your workstation is being built'}</StateTitle>
                <StateMessage>
                  {desktopWarming
                    ? 'almost there — the workstation is up and the desktop view is starting. this usually takes under a minute.'
                    : <>first-time setup takes {WORKSTATION_BOOT_ESTIMATE} — it happens once, and
                  you don't need to wait here.
                  {bootElapsedMin > 0 ? ` (about ${bootElapsedMin} minute${bootElapsedMin === 1 ? '' : 's'} in.)` : ''}</>}
                </StateMessage>
                <StateMessage>
                  chat is ready right now — ask Prest0n anything below while this
                  finishes. this tile switches to your live workstation
                  automatically the moment it's up.
                </StateMessage>
              </StateCard>
              <ReadyNowPanel
                foot={<>…and much more — <KeepCase>Prest0n</KeepCase> keeps learning your business as you go.</>}
              />
            </BootSplit>
          )}

          {/* A bootstrap that fails has no terminal state server-side, so
              without this the spinner counts minutes forever and the user is
              left guessing. Chat still works — say so, and give them a way
              out. */}
          {!target.loading && !target.error && target.provisioning && bootStalled && (
            <BootSplit>
              <StateCard data-testid="workspace-boot-stalled">
                <StateTitle>your workstation is taking longer than expected</StateTitle>
                <StateMessage>
                  setup usually finishes in {WORKSTATION_BOOT_ESTIMATE}
                  {bootElapsedMin > 0 ? `, and this one has been going for about ${bootElapsedMin} minutes` : ''} — it may need a hand.
                </StateMessage>
                {bootStallReason && (
                  <StateMessage data-testid="workspace-boot-stall-reason">
                    {bootStallReason === 'no-infra-doc' || bootStallReason === 'no-instance'
                      ? "we can't locate this organization's workstation record — our team can re-link it."
                      : bootStallReason === 'not-running'
                        ? "the workstation machine isn't running — it may be mid-replacement."
                        : bootStallReason === 'health-probe-failed' || bootStallReason === 'no-ip'
                          ? "the workstation isn't answering yet at its last known address."
                          : bootStallReason === 'missing-credentials' || bootStallReason === 'no-token'
                            ? 'the workstation is up but its secure link needs to be re-issued.'
                            : bootStallReason === 'avatar-page-not-serving'
                              ? 'the workstation is up — the live desktop view is still warming.'
                              : null}
                  </StateMessage>
                )}
                <StateMessage>
                  everything else still works: chat, research, drafting and documents
                  are ready below. reach out to your prest0 point of contact and
                  we'll get the workstation sorted.
                </StateMessage>
                <RetryButton onClick={handleRetry}>check again</RetryButton>
              </StateCard>
              <ReadyNowPanel foot="the workstation is only needed for live desktop tasks — nothing else is blocked while it finishes." />
            </BootSplit>
          )}

          {/* Idle-lifecycle cards (2026-08-18): asleep / waking / archived.
              An asleep doc still carries a stale baseUrl, so these render
              BEFORE any branch that could mount the iframe. */}
          {/* Resize at a stop boundary (2026-09-05, workstation plan PR-10): the
              shape sweep stops the machine for ~2 minutes to give it more
              room and wakes it in the same pass — never a sleep, never a
              wake button; the sweep owns the wake. */}
          {!target.loading && !target.error && (target.lifecycle === 'asleep' || target.lifecycle === 'waking') && target.stopReason === 'resize' && (
            <StateCard data-testid="workspace-resizing-card">
              <Spinner />
              <StateTitle>giving your workstation more room</StateTitle>
              <StateMessage>
                it's moving to a bigger machine — back in about 2 minutes, and
                everything stays exactly as you left it. this tile switches to
                your live workstation automatically.
              </StateMessage>
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'asleep' && target.stopReason !== 'billing' && target.stopReason !== 'resize' && (
            <BootSplit>
              <StateCard data-testid="workspace-asleep-card">
                <StateTitle>🌙 your workstation is asleep</StateTitle>
                <StateMessage>
                  it went to sleep after a quiet stretch to save resources.
                  nothing was lost — you're still signed in to everything on it.
                </StateMessage>
                <StateMessage>
                  waking it takes {WORKSTATION_WAKE_ESTIMATE}, and it starts
                  automatically the moment you ask Prest0n for anything that
                  needs the desktop.
                </StateMessage>
                <RetryButton onClick={() => void requestWake('button')} disabled={wakeBusy}>
                  {wakeBusy ? 'waking…' : 'wake it up'}
                </RetryButton>
              </StateCard>
              <ReadyNowPanel foot="the workstation is only needed for live desktop tasks — nothing else sleeps." />
            </BootSplit>
          )}

          {!target.loading && !target.error && target.lifecycle === 'asleep' && target.stopReason === 'billing' && (
            <StateCard data-testid="workspace-billing-paused-card">
              <StateTitle>your workstation is paused</StateTitle>
              <StateMessage>
                it's paused because of a billing issue. update your payment
                method and it comes back automatically — nothing was deleted.
              </StateMessage>
              <NewTabLink href="/subscription">manage subscription</NewTabLink>
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'waking' && target.stopReason !== 'resize' && (
            <StateCard data-testid="workspace-waking-card">
              <Spinner />
              <StateTitle>waking your workstation</StateTitle>
              <StateMessage>
                everything is exactly as you left it — this takes {WORKSTATION_WAKE_ESTIMATE}.
                this tile switches to your live workstation automatically.
              </StateMessage>
              {bootStalled && (
                <>
                  <StateMessage>
                    it's taking longer than expected — chat and everything else
                    still work below.
                  </StateMessage>
                  <RetryButton onClick={handleRetry}>check again</RetryButton>
                </>
              )}
            </StateCard>
          )}

          {/* Rebuild from snapshot (2026-09-05, workstation plan PR-10): an
              archived workstation coming back — several minutes, not 45–90 s. */}
          {!target.loading && !target.error && target.lifecycle === 'rebuilding' && (
            <StateCard data-testid="workspace-rebuilding-card">
              <Spinner />
              <StateTitle>rebuilding your workstation</StateTitle>
              <StateMessage>
                it's coming back from its saved snapshot — this takes several
                minutes, and most of your sign-ins come right back. this tile
                switches to your live workstation automatically.
              </StateMessage>
              {bootStalled && (
                <>
                  <StateMessage>
                    it's taking longer than expected — chat and everything else
                    still work below.
                  </StateMessage>
                  <RetryButton onClick={handleRetry}>check again</RetryButton>
                </>
              )}
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'archived' && target.stopReason !== 'billing' && (
            <StateCard data-testid="workspace-archived-card">
              <StateTitle>your workstation was archived</StateTitle>
              <StateMessage>
                after a long quiet stretch it was archived to save resources —
                everything important is saved. rebuilding takes {WORKSTATION_BOOT_ESTIMATE},
                and most of your sign-ins come right back with it.
              </StateMessage>
              {/* One-click rebuild: the wake seam answers 'rebuilding' on the
                  self-serve path (uid-plane always; org-plane by DEFAULT since
                  2026-08-18 — orgRebuildEnabled:false is the off switch) and
                  'archived' only when rebuild is disabled or failed to start —
                  then nothing changes here and the support line below covers it. */}
              <RetryButton onClick={() => void requestWake('button')} disabled={wakeBusy}>
                {wakeBusy ? 'rebuilding…' : 'rebuild it'}
              </RetryButton>
              <StateMessage>
                if the button doesn't kick things off, reach out to your prest0
                point of contact and we'll bring it back for you.
              </StateMessage>
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'archived' && target.stopReason === 'billing' && (
            <StateCard data-testid="workspace-archived-billing-card">
              <StateTitle>your workstation is archived</StateTitle>
              <StateMessage>
                it was archived because of a billing issue. update your payment
                method and we'll bring it back — everything important is saved.
              </StateMessage>
              <NewTabLink href="/subscription">manage subscription</NewTabLink>
            </StateCard>
          )}

          {/* Queued for a desktop slot (2026-09-05, workstation plan PR-10):
              the viewer's chat task is waiting for a teammate's session to
              free. Facts from their own ticket; the sweep resumes the task
              and this tile takes the desktop the moment it opens. */}
          {!target.loading && !target.error && !target.provisioning && target.lifecycle === 'active' && queueTicket && (
            <StateCard data-testid="workspace-queued-card">
              <Spinner />
              {queueTicket.status === 'summoned' ? (
                <>
                  <StateTitle>your turn — starting your task now</StateTitle>
                  <StateMessage>
                    a workstation slot just freed up. <KeepCase>Prest0n</KeepCase> is picking
                    your task back up; this tile switches to your live desktop as
                    soon as it opens.
                  </StateMessage>
                </>
              ) : queueTicket.scaling ? (
                <>
                  {/* Scaling (2026-09-05, workstation plan PR-13b): every workstation
                      is busy and the platform is bringing another of the
                      organization's own up for this task — a wake (~90 s) or a
                      new member (~10 min). Facts from the ticket; nothing decided here. */}
                  <StateTitle>
                    {queueTicket.scaling.action === 'waking'
                      ? 'bringing a second workstation up for you'
                      : 'adding a workstation for your team'}
                  </StateTitle>
                  <StateMessage>
                    every workstation is busy right now, so
                    {queueTicket.scaling.action === 'waking' ? ' another one is waking up' : ' a new one is being set up'}
                    {queueTicket.scaling.etaSeconds
                      ? ` — about ${queueTicket.scaling.etaSeconds < 120 ? `${Math.max(30, Math.round(queueTicket.scaling.etaSeconds / 30) * 30)} seconds` : `${Math.max(1, Math.round(queueTicket.scaling.etaSeconds / 60))} min`}`
                      : ''}.
                    this tile switches to it automatically the moment it's ready, and your
                    task picks up on its own — no need to send it again.
                  </StateMessage>
                  <StateMessage>
                    chat and everything else work right now — nothing else is blocked.
                  </StateMessage>
                </>
              ) : (
                <>
                  <StateTitle>
                    {queueTicket.position ? `you're #${queueTicket.position} in line for the workstation` : "you're in line for the workstation"}
                  </StateTitle>
                  <StateMessage>
                    a teammate is using it right now
                    {queueTicket.etaSeconds ? ` — about ${Math.max(1, Math.round(queueTicket.etaSeconds / 60))} min` : ''}.
                    this tile switches over automatically the moment it frees, and your
                    task picks up on its own — no need to send it again.
                  </StateMessage>
                  <StateMessage>
                    chat and everything else work right now — nothing else is blocked.
                  </StateMessage>
                </>
              )}
            </StateCard>
          )}

          {/* Arrival hold (2026-08-20): the account is promoted but the desktop
              renderer hasn't answered yet — show the honest warming card
              instead of mounting an iframe that can only spin. Releases the
              moment the server's desktop probe passes (or 90s fail-open). */}
          {!target.loading && !target.error && !target.provisioning && !queueTicket && desktopHold && target.targetUrl && (
            <StateCard data-testid="workspace-desktop-hold">
              <Spinner />
              <StateTitle>your desktop is warming up</StateTitle>
              <StateMessage>
                almost there — the workstation is up and the desktop view is
                starting. this usually takes under a minute, and this tile
                switches to your live desktop automatically.
              </StateMessage>
            </StateCard>
          )}

          {!target.loading && !target.error && !target.provisioning && !queueTicket && !desktopHold && target.targetUrl && frameStalled && (
            <StateCard data-testid="workspace-frame-stalled">
              <StateTitle>workstation not responding yet</StateTitle>
              <StateMessage>
                your workstation isn't answering — it may still be waking up.
              </StateMessage>
              <RetryButton onClick={handleFrameRetry}>try again</RetryButton>
              <NewTabLink
                href={target.targetUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                or open the workstation in a new tab
              </NewTabLink>
            </StateCard>
          )}

          {!target.loading && !target.error && !target.provisioning && !queueTicket && !desktopHold && target.targetUrl && !frameStalled && (
            <StyledIframe
              key={iframeKey}
              ref={bindIframe}
              $embeddedHeight={embeddedHeight}
              title="Prest0n desktop"
              src={embedSrc ?? target.targetUrl}
              onLoad={() => { frameLoadedRef.current = true; postShellHello(); }}
              // First-party VM app — needs mic for voice WS, WebRTC for
              // lip-sync, autoplay for PCM playback, fullscreen for noVNC
              // expand, clipboard for desktop ops.
              allow="microphone; camera; autoplay; fullscreen; clipboard-read; clipboard-write"
              // sandbox intentionally omitted: the embedded VM app needs
              // unrestricted access to its own origin for WebSocket auth,
              // service workers, and noVNC. The VM app is first-party
              // Prest0n, so the sandbox provides no real safety here while
              // breaking mic/WebRTC/WS in practice. See REALTIME_DESKTOP_NOTES.md.
            />
          )}
        </Frame>

        <GlobalFooter />
      </Page>
    </>
  );
};

export default AgentActivity;
